import { useEffect, useState } from 'react'
import { alsDauer, api } from '../api/client'

interface Hinweis {
  art: string
  schwere: string
  meldung: string
  potenzialMinuten: number | null
}

interface Kursbewertung {
  kursId: number
  kursnummer: string
  verkehrstage: string
  dienstdauer: number
  arbeitszeit: number
  produktiv: number
  leerfahrt: number
  ruestzeit: number
  wendezeit: number
  pause: number
  produktivgrad: number
  anzahlFahrten: number
  geteilterDienst: boolean
  hinweise: Hinweis[]
}

interface Gesamtbild {
  anzahlKurse: number
  arbeitszeitGesamt: number
  produktivGesamt: number
  produktivgradSchnitt: number
  wendezeitGesamt: number
  potenzialMinuten: number
  kurse: Kursbewertung[]
  beobachtungen: string[]
}

/** Farbskala: unter 65 Prozent rot, bis 75 orange, darüber grün. */
function gradFarbe(grad: number): string {
  if (grad < 0.65) return 'var(--rot-500)'
  if (grad < 0.75) return 'var(--orange-500)'
  return 'var(--tuerkis-600)'
}

/** Waagerechter Balken, der die Zusammensetzung eines Dienstes zeigt. */
function Zusammensetzung({ k }: { k: Kursbewertung }) {
  const gesamt = k.arbeitszeit || 1
  const teile = [
    { wert: k.produktiv, farbe: 'var(--navy-700)', text: 'Linienfahrt' },
    { wert: k.leerfahrt, farbe: 'var(--grau-500)', text: 'Leerfahrt' },
    { wert: k.ruestzeit, farbe: 'var(--navy-500)', text: 'Rüstzeit' },
    { wert: k.wendezeit, farbe: 'var(--orange-400)', text: 'Standzeit' },
  ].filter((t) => t.wert > 0)

  return (
    <div style={{ display: 'flex', height: 22, borderRadius: 4, overflow: 'hidden', minWidth: 200 }}>
      {teile.map((t, i) => (
        <div
          key={i}
          title={`${t.text}: ${alsDauer(t.wert)}`}
          style={{
            width: `${(t.wert / gesamt) * 100}%`,
            background: t.farbe,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.66rem',
            color: '#fff',
            fontWeight: 600,
          }}
        >
          {t.wert / gesamt > 0.12 ? Math.round((t.wert / gesamt) * 100) + '%' : ''}
        </div>
      ))}
    </div>
  )
}

export function DienstanalyseSeite() {
  const [daten, setDaten] = useState<Gesamtbild | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [offen, setOffen] = useState<number | null>(null)

  useEffect(() => {
    api
      .get<Gesamtbild>('/api/analyse/dienste')
      .then(setDaten)
      .catch((ex) => setFehler(ex instanceof Error ? ex.message : 'Laden fehlgeschlagen'))
  }, [])

  if (fehler) return <div className="meldung fehler">{fehler}</div>
  if (!daten) return <div className="laden">Dienste werden ausgewertet …</div>

  const unproduktiv = daten.arbeitszeitGesamt - daten.produktivGesamt

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Dienstanalyse</h1>
          <p>
            Wie viel der bezahlten Arbeitszeit entfällt tatsächlich auf Linienfahrten? Berechnet
            aus den eingelesenen Kursblättern.
          </p>
        </div>
      </div>

      {daten.anzahlKurse === 0 ? (
        <div className="karte">
          <div className="leer">
            Noch keine Kurse vorhanden. Laden Sie in der Kursbibliothek Kursblätter hoch.
          </div>
        </div>
      ) : (
        <>
          <div className="karten-raster">
            <div className="kennzahl navy">
              <div className="wert">{Math.round(daten.produktivgradSchnitt * 100)}%</div>
              <div className="beschriftung">Produktivgrad über alle Kurse</div>
            </div>
            <div className="kennzahl">
              <div className="wert">{alsDauer(daten.produktivGesamt)}</div>
              <div className="beschriftung">Linienfahrzeit je Woche</div>
            </div>
            <div className="kennzahl orange">
              <div className="wert">{alsDauer(unproduktiv)}</div>
              <div className="beschriftung">nicht auf Linienfahrten</div>
            </div>
            <div className="kennzahl">
              <div className="wert">{daten.anzahlKurse}</div>
              <div className="beschriftung">ausgewertete Kurse</div>
            </div>
          </div>

          {daten.beobachtungen.length > 0 && (
            <div className="karte abschnitt">
              <h2>Was auffällt</h2>
              <ul style={{ lineHeight: 1.8, color: 'var(--grau-700)', paddingLeft: 20, margin: '10px 0 0' }}>
                {daten.beobachtungen.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="tabelle-huelle">
            <table>
              <thead>
                <tr>
                  <th>Kurs</th>
                  <th>Verkehrstage</th>
                  <th>Arbeitszeit</th>
                  <th style={{ minWidth: 220 }}>Zusammensetzung</th>
                  <th>Produktivgrad</th>
                  <th>Fahrten</th>
                  <th>Hinweise</th>
                </tr>
              </thead>
              <tbody>
                {daten.kurse.map((k, i) => (
                  <tr key={`${k.kursId}-${i}`}>
                    <td>
                      <strong>{k.kursnummer}</strong>
                      {k.geteilterDienst && (
                        <span className="etikett orange" style={{ marginLeft: 6 }}>
                          geteilt
                        </span>
                      )}
                    </td>
                    <td style={{ fontSize: '0.83rem' }}>{k.verkehrstage}</td>
                    <td className="zahl">{alsDauer(k.arbeitszeit)}</td>
                    <td>
                      <Zusammensetzung k={k} />
                    </td>
                    <td>
                      <strong style={{ color: gradFarbe(k.produktivgrad), fontSize: '1.05rem' }}>
                        {Math.round(k.produktivgrad * 100)}%
                      </strong>
                    </td>
                    <td className="zahl">{k.anzahlFahrten}</td>
                    <td>
                      {k.hinweise.length === 0 ? (
                        <span className="etikett gruen">unauffällig</span>
                      ) : (
                        <button
                          className="knopf-schlicht"
                          style={{
                            padding: '4px 11px',
                            fontSize: '0.8rem',
                            borderColor: k.hinweise.some((h) => h.schwere === 'SPERRE')
                              ? 'var(--rot-500)'
                              : 'var(--orange-500)',
                            color: k.hinweise.some((h) => h.schwere === 'SPERRE')
                              ? 'var(--rot-500)'
                              : 'var(--orange-600)',
                          }}
                          onClick={() => setOffen(offen === i ? null : i)}
                        >
                          {k.hinweise.length} {offen === i ? '▴' : '▾'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {offen !== null && daten.kurse[offen] && (
            <div className="karte abschnitt" style={{ marginTop: 14 }}>
              <h2>Kurs {daten.kurse[offen].kursnummer}</h2>
              <ul style={{ lineHeight: 1.9, paddingLeft: 20, margin: '10px 0 0' }}>
                {daten.kurse[offen].hinweise.map((h, i) => (
                  <li key={i}>
                    <span
                      className={`etikett ${h.schwere === 'SPERRE' ? 'rot' : 'orange'}`}
                      style={{ marginRight: 8 }}
                    >
                      {h.schwere === 'SPERRE' ? 'Verstoß' : 'Hinweis'}
                    </span>
                    {h.meldung}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="legende" style={{ marginTop: 14 }}>
            <span>
              <i style={{ background: 'var(--navy-700)' }} /> Linienfahrt — dafür zahlt der
              Auftraggeber
            </span>
            <span>
              <i style={{ background: 'var(--grau-500)' }} /> Leerfahrt
            </span>
            <span>
              <i style={{ background: 'var(--navy-500)' }} /> Rüstzeit
            </span>
            <span>
              <i style={{ background: 'var(--orange-400)' }} /> Standzeit im Dienst
            </span>
          </div>
        </>
      )}
    </>
  )
}
